import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { TYPE_COLORS, TYPE_ICONS, TYPE_LABELS } from "./constants";

export default function TypeSummaryCards({ loc, items, typeCounts, active, onToggle }) {
  // For each type, compute also breakdown by item name (for dialog later)
  const types = Object.keys(typeCounts).sort();
  const [breakdownType, setBreakdownType] = useState(null);

  const breakdown = useMemo(() => {
    if (!breakdownType) return null;
    const filtered = items.filter((it) => (it.item_type || "echipament") === breakdownType);
    const map = {};
    for (const it of filtered) {
      const n = (it.name || "—").trim();
      map[n] = (map[n] || 0) + (Number(it.quantity) > 0 ? Number(it.quantity) : 1);
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [breakdownType, items]);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-3" data-testid={`type-cards-${loc}`}>
        {types.map((t) => {
          const cnt = typeCounts[t];
          const isActive = active === t;
          const c = TYPE_COLORS[t] || TYPE_COLORS.echipament;
          return (
            <button
              key={t}
              type="button"
              onClick={() => { onToggle(t); setBreakdownType(t); }}
              className={`text-left p-3 border-2 transition-all rounded ${isActive ? `${c.activeBg} ${c.activeText} border-transparent shadow-md scale-[1.02]` : `${c.bg} ${c.border} ${c.text} hover:scale-[1.01] hover:shadow-sm`}`}
              data-testid={`type-card-${loc}-${t}`}
              title={`Vezi defalcare ${TYPE_LABELS[t] || t}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl leading-none">{TYPE_ICONS[t] || "•"}</span>
                  <div>
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${isActive ? "text-white/80" : "opacity-70"}`}>{TYPE_LABELS[t] || t}</div>
                    <div className="font-display text-2xl font-extrabold leading-tight">{cnt}</div>
                  </div>
                </div>
                <div className={`text-[10px] uppercase tracking-wider font-bold ${isActive ? "text-white" : "opacity-50"}`}>
                  {isActive ? "✓ ACTIV" : "Click"}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Breakdown dialog */}
      {breakdownType && breakdown && (
        <Dialog open onOpenChange={(v) => !v && setBreakdownType(null)}>
          <DialogContent className="max-w-md" data-testid={`type-breakdown-${loc}-${breakdownType}`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-2xl">{TYPE_ICONS[breakdownType]}</span>
                <span>{TYPE_LABELS[breakdownType]} · {loc}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs text-zinc-500 mb-2">
              Defalcare după denumire ({breakdown.length} {breakdown.length === 1 ? "tip" : "tipuri"} distincte · {breakdown.reduce((s, [, n]) => s + n, 0)} bucăți total)
            </div>
            {breakdown.length === 0 ? (
              <div className="text-sm text-zinc-400 italic">Niciun articol.</div>
            ) : (
              <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                {breakdown.map(([name, count]) => (
                  <div key={name} className="flex items-center justify-between p-2 border border-zinc-200 rounded hover:bg-zinc-50" data-testid={`breakdown-row-${name}`}>
                    <span className="text-sm font-medium text-zinc-800 truncate">{name}</span>
                    <span className="font-display text-lg font-extrabold text-[var(--primary)] ml-3 tabular-nums">× {count}</span>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
